import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState, useAppDispatch } from '../../redux/store'
import { Action } from '../../types/actionTypes'
import { fetchMyActions } from '../../redux/actorAction/actorActionAction'
import { updateAction } from '../../redux/actions/actionActions'
import { EmptyState } from '../common/EmptyState'
import { useTranslation } from 'react-i18next'
import { formatTitle } from '../../utils/formatTitle'
import './ActionsBoard.css'

type ApproveFilter = 'all' | 'critical' | 'oldest'
type AssignedFilter = 'all' | 'pastdue' | 'week'

const PRIORITY_LABEL: Record<number, string> = {
  1: 'Critical',
  2: 'High',
  3: 'Medium',
  4: 'Low',
}

const PRIORITY_PILL: Record<number, string> = {
  1: 'p-crit',
  2: 'p-high',
  3: 'p-med',
  4: 'p-low',
}

function daysBetween(from: Date, to: Date) {
  const ms = to.getTime() - from.getTime()
  return Math.max(0, Math.floor(ms / 86400000))
}

function isPastDue(action: Action) {
  if (!action.DateFin) return false
  const end = new Date(action.DateFin)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  end.setHours(0, 0, 0, 0)
  const status = (action.Status || '').trim().toLowerCase()
  const done = status === 'terminé' || status === 'termine'
  return end < today && !done
}

function isDueThisWeek(action: Action) {
  if (!action.DateFin) return false
  const end = new Date(action.DateFin)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const weekEnd = new Date(today)
  weekEnd.setDate(today.getDate() + 7)
  return end >= today && end <= weekEnd
}

function formatDue(date?: Date | string) {
  if (!date) return '—'
  const d = new Date(date)
  if (Number.isNaN(d.getTime())) return '—'
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  return `${dd}/${mm}/${d.getFullYear()}`
}

function statusLabel(status?: string) {
  const s = (status || '').trim()
  const key = s.toLowerCase()
  if (key === 'en cours') return 'In Progress'
  if (key === 'a faire' || key === 'à faire') return 'To Do'
  if (key === 'terminé' || key === 'termine') return 'Done'
  return s || '—'
}

function statusPillClass(status?: string) {
  const key = (status || '').trim().toLowerCase()
  if (key === 'en cours') return 'p-prog'
  if (key === 'a faire' || key === 'à faire') return 'p-todo'
  if (key === 'terminé' || key === 'termine') return 'p-low'
  return 'p-mute'
}

function initials(name?: string) {
  if (!name) return '?'
  const parts = name.trim().split(/[\s._-]+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return name.substring(0, 2).toUpperCase()
}

function waitDays(action: Action) {
  const raw = action.CreationDate || action.UpdateDate
  if (!raw) return 0
  return daysBetween(new Date(raw), new Date())
}

type TaskCardProps = {
  action: Action
  mode: 'approve' | 'assigned'
  onMarkDone?: (action: Action) => void
  markingId?: string | null
}

function TaskCard({ action, mode, onMarkDone, markingId }: TaskCardProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const open = () => navigate(`/actiond/${action._id}`)
  const priority = Number(action.Priority) || 0
  const prioLabel = t(`priority.${priority}`, {
    defaultValue: PRIORITY_LABEL[priority] || String(priority || '—'),
  })
  const wait = waitDays(action)
  const late = isPastDue(action)
  const statusText = t(statusLabel(action.Status), {
    defaultValue: statusLabel(action.Status),
  })

  return (
    <div
      className="ab-task-card"
      role="link"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          open()
        }
      }}
    >
      <div className="ab-tc-top">
        <div>
          <h4>{formatTitle(action.ActionName || '')}</h4>
          <p>{action.Description || action.ProjetName || '—'}</p>
        </div>
        {mode === 'approve' ? (
          <span className="ab-pill p-med">
            <span className="ab-dot" />
            {t('homePage.myTasks.waitingDays', {
              count: wait,
              defaultValue: `waiting ${wait} days`,
            })}
          </span>
        ) : (
          <span className={`ab-pill ${late ? 'p-crit' : 'p-mute'}`}>
            <span className="ab-dot" />
            {late
              ? t('homePage.myTasks.pastDue', { defaultValue: 'Past due' })
              : t('homePage.myTasks.onTrack', { defaultValue: 'On track' })}
          </span>
        )}
      </div>

      <div className="ab-tc-chips">
        {action.TypeAction && (
          <span className="ab-pill p-mute">{action.TypeAction}</span>
        )}
        <span className={`ab-pill ${statusPillClass(action.Status)}`}>
          <span className="ab-dot" />
          {statusText}
        </span>
        {priority > 0 && (
          <span className={`ab-pill ${PRIORITY_PILL[priority] || 'p-mute'}`}>
            <span className="ab-dot" />
            {prioLabel}
          </span>
        )}
        <span className="ab-tc-due">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
            <rect x="2.5" y="3.5" width="11" height="10" rx="1.5" />
            <path d="M2.5 6.5h11M5.5 2.5v2M10.5 2.5v2" />
          </svg>
          {formatDue(action.DateFin)}
        </span>
      </div>

      <div className="ab-tc-foot">
        <span className="ab-av">{initials(action.CreatedBy)}</span>
        <span className="ab-tc-meta">
          <b>{action.CreatedBy || '—'}</b>
          {action.MissionName || '—'}
        </span>
        <span
          className="ab-tc-actions"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {mode === 'approve' ? (
            <>
              <button type="button" className="ab-btn ab-btn-primary" onClick={open}>
                {t('homePage.myTasks.approve', { defaultValue: 'Approve' })}
              </button>
              <button type="button" className="ab-btn ab-btn-ghost" onClick={open}>
                {t('homePage.myTasks.requestChanges', {
                  defaultValue: 'Request changes',
                })}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="ab-btn ab-btn-ghost"
                disabled={markingId === action._id}
                onClick={() => onMarkDone?.(action)}
              >
                {t('homePage.myTasks.markDone', { defaultValue: 'Mark as done' })}
              </button>
              <button type="button" className="ab-btn ab-btn-ghost" onClick={open}>
                {t('homePage.myTasks.open', { defaultValue: 'Open' })}
              </button>
            </>
          )}
        </span>
      </div>
    </div>
  )
}

export const ActionsBoard = () => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [approveFilter, setApproveFilter] = useState<ApproveFilter>('all')
  const [assignedFilter, setAssignedFilter] = useState<AssignedFilter>('all')
  const [markingId, setMarkingId] = useState<string | null>(null)

  const { actions, loading, error } = useSelector(
    (state: RootState) => state.acteurAction
  )

  useEffect(() => {
    dispatch(fetchMyActions())
  }, [dispatch])

  const toApprove = actions?.Approbateur || []
  const assignedTasks = actions?.Responsable || []

  const filteredApprove = useMemo(() => {
    let rows = [...toApprove]
    if (approveFilter === 'critical') {
      rows = rows.filter((a) => Number(a.Priority) === 1)
    }
    if (approveFilter === 'oldest') {
      rows.sort((a, b) => {
        const da = a.CreationDate ? new Date(a.CreationDate).getTime() : 0
        const db = b.CreationDate ? new Date(b.CreationDate).getTime() : 0
        return da - db
      })
    }
    return rows
  }, [toApprove, approveFilter])

  const filteredAssigned = useMemo(() => {
    let rows = [...assignedTasks]
    if (assignedFilter === 'pastdue') {
      rows = rows.filter(isPastDue)
    } else if (assignedFilter === 'week') {
      rows = rows.filter(isDueThisWeek)
    }
    return rows
  }, [assignedTasks, assignedFilter])

  const pastDueCount = useMemo(
    () => assignedTasks.filter(isPastDue).length,
    [assignedTasks]
  )

  const longestWait = useMemo(() => {
    if (!toApprove.length) return 0
    return Math.max(...toApprove.map(waitDays))
  }, [toApprove])

  const handleMarkDone = async (action: Action) => {
    if (!action._id) return
    setMarkingId(action._id)
    try {
      await dispatch(
        updateAction({
          updatedAction: { ...action, Status: 'Terminé' },
          _id: action._id,
        })
      ).unwrap()
      await dispatch(fetchMyActions())
    } catch {
      // keep UI quiet; detail page shows alerts for failures
    } finally {
      setMarkingId(null)
    }
  }

  if (loading) {
    return (
      <div className="ab-loading">
        <div className="ab-spinner" />
      </div>
    )
  }

  if (error) {
    return <div className="ab-error">{error}</div>
  }

  return (
    <div className="ab-board">
      <div className="ab-col">
        <div className="ab-card">
          <div className="ab-card-head">
            <h3>
              {t('homePage.myTasks.toApprove.title', {
                defaultValue: 'Tasks to approve',
              })}
            </h3>
            <span className="ab-pill p-med">
              <span className="ab-dot" />
              {t('homePage.myTasks.waitingOnYou', {
                count: toApprove.length,
                defaultValue: `${toApprove.length} waiting on you`,
              })}
            </span>
          </div>
          <div className="ab-toolbar">
            <button
              type="button"
              className={`ab-chip${approveFilter === 'all' ? ' on' : ''}`}
              onClick={() => setApproveFilter('all')}
            >
              {t('listPages.all', { defaultValue: 'All' })}
            </button>
            <button
              type="button"
              className={`ab-chip${approveFilter === 'critical' ? ' on' : ''}`}
              onClick={() => setApproveFilter('critical')}
            >
              {t('homePage.missionPriority.critical')}
            </button>
            <button
              type="button"
              className={`ab-chip${approveFilter === 'oldest' ? ' on' : ''}`}
              onClick={() => setApproveFilter('oldest')}
            >
              {t('homePage.myTasks.oldestFirst', {
                defaultValue: 'Oldest first',
              })}
            </button>
            <span className="ab-count-note">
              {t('homePage.myTasks.longestWait', {
                count: longestWait,
                defaultValue: `Longest wait: ${longestWait} days`,
              })}
            </span>
          </div>
          <div className="ab-task-list">
            {filteredApprove.length > 0 ? (
              filteredApprove.map((action) => (
                <TaskCard
                  key={action._id}
                  action={action}
                  mode="approve"
                />
              ))
            ) : (
              <EmptyState
                message={t('homePage.myTasks.emptyState', {
                  defaultValue: 'No tasks to approve at the moment.',
                })}
              />
            )}
          </div>
        </div>
      </div>

      <div className="ab-col">
        <div className="ab-card">
          <div className="ab-card-head">
            <h3>
              {t('homePage.myTasks.assigned.title', {
                defaultValue: 'Assigned to me',
              })}
            </h3>
            {pastDueCount > 0 ? (
              <span className="ab-pill p-crit">
                <span className="ab-dot" />
                {t('homePage.myTasks.pastDueCount', {
                  count: pastDueCount,
                  defaultValue: `${pastDueCount} past due`,
                })}
              </span>
            ) : (
              <span className="ab-pill p-mute">
                <span className="ab-dot" />
                {t('homePage.myTasks.onTrack', { defaultValue: 'On track' })}
              </span>
            )}
          </div>
          <div className="ab-toolbar">
            <button
              type="button"
              className={`ab-chip${assignedFilter === 'all' ? ' on' : ''}`}
              onClick={() => setAssignedFilter('all')}
            >
              {t('listPages.all', { defaultValue: 'All' })}
            </button>
            <button
              type="button"
              className={`ab-chip${assignedFilter === 'pastdue' ? ' on' : ''}`}
              onClick={() => setAssignedFilter('pastdue')}
            >
              {t('homePage.myTasks.pastDue', { defaultValue: 'Past due' })}
            </button>
            <button
              type="button"
              className={`ab-chip${assignedFilter === 'week' ? ' on' : ''}`}
              onClick={() => setAssignedFilter('week')}
            >
              {t('homePage.myTasks.thisWeek', { defaultValue: 'This week' })}
            </button>
            <span className="ab-count-note">
              {t('homePage.myTasks.taskCount', {
                count: filteredAssigned.length,
                defaultValue: `${filteredAssigned.length} tasks`,
              })}
            </span>
          </div>
          <div className="ab-task-list">
            {filteredAssigned.length > 0 ? (
              filteredAssigned.map((action) => (
                <TaskCard
                  key={action._id}
                  action={action}
                  mode="assigned"
                  onMarkDone={handleMarkDone}
                  markingId={markingId}
                />
              ))
            ) : (
              <EmptyState message={t('homePage.myTasks.empty')} />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
